import React, { useMemo, useCallback } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import clsx from "clsx";
import { User, Building2 } from "lucide-react";
import { AgendaJobData } from "../agenda.types";
import {
	DAY_COL_WIDTH,
	formatJobDateRange,
	getFontSizeForRowHeight,
	getJobColorClass,
	HOUR_COL_WIDTH,
	WEEK_COL_WIDTH,
	WeekRange,
} from "./utils";

function hashColor(id: string): string {
	let hash = 0;
	for (let i = 0; i < id.length; i++) {
		hash = (hash << 5) - hash + id.charCodeAt(i);
		hash = hash & hash;
	}
	const hue = Math.abs(hash) % 360;
	return `hsl(${hue}, 70%, 45%)`;
}

function getInitials(
	profileId: string,
	profilesMap?: Record<string, { first_name?: string; last_name?: string }>
): string {
	if (profilesMap && profilesMap[profileId]) {
		const p = profilesMap[profileId];
		return (
			(p.first_name?.[0] ?? "") + (p.last_name?.[0] ?? "")
		).toUpperCase();
	}
	return "";
}

interface JobBarProps {
	job: AgendaJobData;
	daysInRange: Date[];
	viewMode: "day" | "week" | "hour";
	weeksInRange: WeekRange[];
	hoursInRange: number[];
	hourViewDate?: string;
	colWidth?: number;
	rowHeight?: number;
	onJobClick: (job: AgendaJobData) => void;
	profileColors?: Record<string, string>;
	profilesMap?: Record<string, { first_name?: string; last_name?: string }>;
}

function parseTimeToHours(time: string | null | undefined): number | null {
	if (!time) return null;
	const parts = time.split(":");
	if (parts.length < 2) return null;
	return parseInt(parts[0], 10) + parseInt(parts[1], 10) / 60;
}

function getBarPosition(
	job: AgendaJobData,
	daysInRange: Date[],
	viewMode: "day" | "week" | "hour",
	weeksInRange: WeekRange[],
	hoursInRange: number[],
	hourViewDate?: string,
	colWidth?: number
): {
	left: number;
	width: number;
	roundLeft: boolean;
	roundRight: boolean;
} | null {
	if (!job.incoming_order_date || !job.outgoing_order_date) return null;

	if (viewMode === "hour") {
		const firstHour = hoursInRange[0];
		const lastHour = hoursInRange[hoursInRange.length - 1];
		const hourW = colWidth ?? HOUR_COL_WIDTH;
		const totalWidth = hoursInRange.length * hourW;

		const dayStr =
			hourViewDate ??
			(daysInRange.length > 0
				? daysInRange[0].toISOString().split("T")[0]
				: new Date().toISOString().split("T")[0]);

		const startsToday = job.incoming_order_date === dayStr;
		const endsToday = job.outgoing_order_date === dayStr;

		const inTime = parseTimeToHours(job.incoming_order_time);
		const outTime = parseTimeToHours(job.outgoing_order_time);

		let startHour = firstHour;
		let endHour = lastHour + 1;
		let roundLeft = true;
		let roundRight = true;

		if (startsToday && inTime !== null) {
			startHour = Math.max(firstHour, inTime);
			roundLeft = inTime >= firstHour;
		} else if (!startsToday) {
			roundLeft = false;
		}

		if (endsToday && outTime !== null) {
			endHour = Math.min(lastHour + 1, outTime);
			roundRight = outTime <= lastHour + 1;
		} else if (!endsToday) {
			roundRight = false;
		}

		if (endHour <= startHour) {
			endHour = Math.min(startHour + 0.5, lastHour + 1);
		}

		const left =
			((startHour - firstHour) / hoursInRange.length) * totalWidth;
		const width =
			((endHour - startHour) / hoursInRange.length) * totalWidth;

		return { left, width: Math.max(width, 4), roundLeft, roundRight };
	}

	const jobStart = new Date(
		new Date(job.incoming_order_date as string).toDateString()
	);
	const jobEnd = new Date(
		new Date(job.outgoing_order_date as string).toDateString()
	);

	if (viewMode === "day") {
		if (daysInRange.length === 0) return null;

		const rangeStart = new Date(daysInRange[0].toDateString());
		const rangeEnd = new Date(
			daysInRange[daysInRange.length - 1].toDateString()
		);

		if (jobEnd < rangeStart || jobStart > rangeEnd) return null;

		const msPerDay = 86400000;
		const clampedStart = jobStart < rangeStart ? rangeStart : jobStart;
		const clampedEnd = jobEnd > rangeEnd ? rangeEnd : jobEnd;

		const startIdx =
			(clampedStart.getTime() - rangeStart.getTime()) / msPerDay;
		const endIdx = (clampedEnd.getTime() - rangeStart.getTime()) / msPerDay;

		const dayW = colWidth ?? DAY_COL_WIDTH;
		return {
			left: startIdx * dayW,
			width: (endIdx - startIdx + 1) * dayW,
			roundLeft: jobStart >= rangeStart,
			roundRight: jobEnd <= rangeEnd,
		};
	}

	if (weeksInRange.length === 0) return null;

	const rangeStart = weeksInRange[0].start;
	const rangeEnd = weeksInRange[weeksInRange.length - 1].end;

	if (jobEnd < rangeStart || jobStart > rangeEnd) return null;

	const clampedStart = jobStart < rangeStart ? rangeStart : jobStart;
	const clampedEnd = jobEnd > rangeEnd ? rangeEnd : jobEnd;

	const weekW = colWidth ?? WEEK_COL_WIDTH;
	const pixelsPerDay = weekW / 7;
	const msPerDay = 86400000;
	const startDayOffset =
		(clampedStart.getTime() - rangeStart.getTime()) / msPerDay;
	const endDayOffset =
		(clampedEnd.getTime() - rangeStart.getTime()) / msPerDay;

	return {
		left: startDayOffset * pixelsPerDay,
		width: (endDayOffset - startDayOffset + 1) * pixelsPerDay,
		roundLeft: jobStart >= rangeStart,
		roundRight: jobEnd <= rangeEnd,
	};
}

const CourierIcon: React.FC<{
	job: AgendaJobData;
	barWidth: number;
	barH: number;
	profileColors?: Record<string, string>;
	profilesMap?: Record<string, { first_name?: string; last_name?: string }>;
}> = ({ job, barWidth, barH, profileColors, profilesMap }) => {
	if (!job.delivery_courier_type) return null;
	const isShort = barWidth < 60;
	const iconSize = isShort ? 12 : Math.min(barH - 2, 16);

	if (job.delivery_courier_type === "company") {
		return (
			<TooltipPrimitive.Root>
				<TooltipPrimitive.Trigger asChild>
					<div
						className="absolute flex items-center justify-center"
						style={{
							right: isShort ? "50%" : 2,
							top: "50%",
							transform: isShort
								? "translate(50%, -50%)"
								: "translateY(-50%)",
							width: iconSize,
							height: iconSize,
						}}
					>
						<Building2
							className="text-white/80"
							style={{
								width: iconSize - 2,
								height: iconSize - 2,
							}}
						/>
					</div>
				</TooltipPrimitive.Trigger>
				{job.delivery_courier_company_name && (
					<TooltipPrimitive.Content
						className="z-50 rounded-md border bg-popover px-2 py-1 text-xs text-popover-foreground shadow-md"
						sideOffset={4}
					>
						{job.delivery_courier_company_name}
					</TooltipPrimitive.Content>
				)}
			</TooltipPrimitive.Root>
		);
	}

	const profileId = job.delivery_profile_id;
	const color = profileId
		? profileColors?.[profileId] || hashColor(profileId)
		: "#6b7280";
	const initials = profileId ? getInitials(profileId, profilesMap) : "";

	if (isShort || !initials) {
		return (
			<div
				className="absolute flex items-center justify-center"
				style={{
					right: isShort ? "50%" : 2,
					top: "50%",
					transform: isShort
						? "translate(50%, -50%)"
						: "translateY(-50%)",
					width: iconSize,
					height: iconSize,
				}}
			>
				<User
					style={{ width: iconSize - 2, height: iconSize - 2, color }}
				/>
			</div>
		);
	}

	return (
		<div
			className="absolute flex items-center justify-center rounded-full text-white font-bold"
			style={{
				right: 2,
				top: "50%",
				transform: "translateY(-50%)",
				width: iconSize,
				height: iconSize,
				backgroundColor: color,
				fontSize: Math.max(7, iconSize * 0.55),
				lineHeight: 1,
			}}
		>
			{initials}
		</div>
	);
};

export const JobBar: React.FC<JobBarProps> = React.memo(
	({
		job,
		daysInRange,
		viewMode,
		weeksInRange,
		hoursInRange,
		hourViewDate,
		colWidth,
		rowHeight = 20,
		onJobClick,
		profileColors,
		profilesMap,
	}) => {
		const pos = useMemo(
			() =>
				getBarPosition(
					job,
					daysInRange,
					viewMode,
					weeksInRange,
					hoursInRange,
					hourViewDate,
					colWidth
				),
			[
				job,
				daysInRange,
				viewMode,
				weeksInRange,
				hoursInRange,
				hourViewDate,
				colWidth,
			]
		);

		const handleClick = useCallback(() => {
			onJobClick(job);
		}, [job, onJobClick]);

		if (!pos) return null;

		const barH = Math.max(8, rowHeight - 6);
		const barTop = Math.floor((rowHeight - barH) / 2);
		const barFs = getFontSizeForRowHeight(rowHeight);
		const patientName = [job.patient_first_name, job.patient_last_name]
			.filter(Boolean)
			.join(" ");
		const label = [job.code, job.title, patientName]
			.filter(Boolean)
			.join(" ");

		return (
			<TooltipPrimitive.Root>
				<TooltipPrimitive.Trigger asChild>
					<div
						className={clsx(
							"absolute cursor-pointer shadow-sm overflow-hidden",
							getJobColorClass(job.status, job.is_untouched) ??
								"bg-gray-300",
							{
								"rounded-l-md": pos.roundLeft,
								"rounded-r-md": pos.roundRight,
							}
						)}
						style={{
							left: pos.left,
							width: pos.width,
							top: barTop,
							height: barH,
						}}
						onClick={handleClick}
						data-testid={`agenda-job-bar-${job.id}`}
						aria-label={`Job ${job.code} bar`}
					>
						{pos.width >= 60 && barH >= 12 && (
							<span
								className="absolute inset-0 flex items-center px-1.5 font-medium text-white truncate leading-none"
								style={{
									fontSize: barFs - 1,
									paddingRight: job.delivery_courier_type
										? Math.min(barH + 2, 20)
										: undefined,
								}}
							>
								{label}
							</span>
						)}
						{job.delivery_courier_type && (
							<CourierIcon
								job={job}
								barWidth={pos.width}
								barH={barH}
								profileColors={profileColors}
								profilesMap={profilesMap}
							/>
						)}
					</div>
				</TooltipPrimitive.Trigger>
				<TooltipPrimitive.Content
					className="z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95"
					sideOffset={4}
				>
					<div className="font-medium">
						{job.code} — {job.title}
						{patientName && (
							<span className="font-normal text-muted-foreground">
								{" · "}
								{patientName}
							</span>
						)}
					</div>
					<div className="text-xs text-muted-foreground">
						{formatJobDateRange(job)}
					</div>
				</TooltipPrimitive.Content>
			</TooltipPrimitive.Root>
		);
	}
);
